<template>
  <main class="home-prototype single-film-prototype single-person-prototype search-prototype">
    <section class="home-top-panel g-0 position-relative">
      <HeroTopPanel
        :logo-src="logoPath"
        login-path="/login?redirect=%2Fsearch"
        :show-background="false"
        content-class="home-top-panel__content"
        @open-mobile-search="openMobileSearchOverlay"
      />
    </section>

    <section class="home-content-section search-content-section">
      <div class="container g-0">
        <div class="row g-0 search-layout">
          <div class="col-7 g-0 search-content">
            <h1 class="home-section-title" data-testid="search-title">Search</h1>
            <form class="search-page-form" @submit.prevent="submitSearch">
              <input
                v-model.trim="searchInput"
                class="search-page-input"
                data-testid="search-query"
                type="search"
                placeholder="Search films, shows, people and lists..."
              >
              <button class="sorting-btn filter-btn search-page-submit" type="submit">
                Search
              </button>
            </form>

            <p v-if="loading" class="home-empty-state">Searching...</p>
            <p v-else-if="error" class="home-settings-message home-settings-message--error">
              {{ error }}
            </p>
            <template v-else>
              <nav class="search-tabs" aria-label="Search result types">
                <a
                  v-for="tab in visibleTabs"
                  :key="tab.key"
                  :href="searchUrl(tab.key)"
                  :class="['home-content-tabs__link', { 'home-content-tabs__link--active': tab.key === activeType }]"
                  data-testid="search-tab"
                  @click.prevent="setActiveType(tab.key)"
                >
                  {{ tab.label }} ({{ tab.count }})
                </a>
              </nav>

              <div v-if="results.length" class="search-results">
                <a
                  v-for="result in results"
                  :key="`${result.type}-${result.id}`"
                  :class="[
                    'search-result-row',
                    { 'search-result-row--with-image': result.image_url },
                  ]"
                  data-testid="search-result"
                  :href="result.url"
                >
                  <img
                    v-if="result.image_url"
                    class="search-result-row__image"
                    data-testid="search-result-image"
                    :src="result.image_url"
                    :alt="`${result.title} image`"
                  >
                  <span class="search-result-row__body">
                    <span>
                      <span class="search-result-row__title">{{ result.title }}</span>
                      <span v-if="result.subtitle" class="search-result-row__subtitle">
                        {{ result.subtitle }}
                      </span>
                    </span>
                    <span class="search-result-row__type">{{ result.type }}</span>
                  </span>
                </a>
              </div>
              <p v-else-if="query" class="home-empty-state">
                No results.
              </p>
              <p v-else class="home-empty-state">
                Type a search query.
              </p>

              <nav v-if="showPagination" class="search-pagination search-pagination--with-mobile" aria-label="Search pagination">
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: !pagination.has_previous }">
                    <button type="button" class="page-link" @click="setPage(pagination.page - 1)">
                      Previous
                    </button>
                  </li>
                  <li
                    v-for="pageItem in paginationItems"
                    :key="pageItem.key"
                    class="page-item"
                    :class="{ active: pageItem.page === pagination.page, disabled: pageItem.ellipsis }"
                  >
                    <span v-if="pageItem.ellipsis" class="page-link" aria-hidden="true">
                      {{ pageItem.label }}
                    </span>
                    <button
                      v-else
                      type="button"
                      class="page-link"
                      :aria-label="`Go to page ${pageItem.page}`"
                      @click="setPage(pageItem.page)"
                    >
                      {{ pageItem.label }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: !pagination.has_next }">
                    <button type="button" class="page-link" @click="setPage(pagination.page + 1)">
                      Next
                    </button>
                  </li>
                </ul>
                <CompactMobilePagination
                  :current-page="pagination.page"
                  :total-pages="pagination.num_pages"
                  test-id-prefix="search"
                  @page-change="setPage"
                />
              </nav>
            </template>
          </div>
        </div>
      </div>
    </section>

    <MobileSearchOverlay
      v-if="mobileSearchOpen"
      :open="mobileSearchOpen"
      @close="closeMobileSearchOverlay"
    />
  </main>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroTopPanel from '../components/HeroTopPanel.vue'
import MobileSearchOverlay from '../components/MobileSearchOverlay.vue'
import CompactMobilePagination from '../components/CompactMobilePagination.vue'
import '../assets/single-film/bootstrap-lite.css'
import '../assets/single-film/styles.css'
import '../assets/home/styles.css'
import { buildDocumentTitle, setDocumentTitle } from '../utils/documentTitle'
import { compactPaginationItems } from '../utils/pagination'

const route = useRoute()
const router = useRouter()

const logoPath = '/static/spa/branding/logo.png'
const loading = ref(false)
const error = ref('')
const searchInput = ref('')
const query = ref('')
const activeType = ref('films')
const tabs = ref([])
const results = ref([])
const pagination = ref({
  page: 1,
  page_size: 20,
  num_pages: 1,
  count: 0,
  has_next: false,
  has_previous: false,
})
const mobileSearchOpen = ref(false)

const showPagination = computed(() => pagination.value.num_pages > 1)
const visibleTabs = computed(() => tabs.value.filter((tab) => Number(tab.count) > 0))
const paginationItems = computed(() => compactPaginationItems(pagination.value.page, pagination.value.num_pages))

function routeQuery() {
  return String(route.query.q || '').trim()
}

function routeType() {
  return String(route.query.type || 'films')
}

function routePage() {
  return String(route.query.page || '1')
}

function apiUrl() {
  const params = new URLSearchParams({
    q: routeQuery(),
    type: routeType(),
    page: routePage(),
  })
  return `/api/v1/search/?${params.toString()}`
}

function searchUrl(type, page = 1, searchQuery = query.value) {
  const params = new URLSearchParams({
    q: searchQuery,
    type,
  })
  if (page > 1) {
    params.set('page', String(page))
  }
  return `/search?${params.toString()}`
}

function pageUrl(page) {
  return searchUrl(activeType.value, page)
}

async function loadSearchResults() {
  query.value = routeQuery()
  searchInput.value = query.value
  if (!query.value) {
    loading.value = false
    error.value = ''
    activeType.value = routeType()
    tabs.value = []
    results.value = []
    pagination.value = {
      page: 1,
      page_size: 20,
      num_pages: 1,
      count: 0,
      has_next: false,
      has_previous: false,
    }
    return
  }

  loading.value = true
  error.value = ''
  try {
    const response = await fetch(apiUrl(), {
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) {
      error.value = 'Unable to load search results.'
      return
    }
    const payload = await response.json()
    query.value = payload.query || query.value
    activeType.value = payload.active_type || routeType()
    tabs.value = Array.isArray(payload.tabs) ? payload.tabs : []
    results.value = Array.isArray(payload.results) ? payload.results : []
    pagination.value = payload.pagination || pagination.value
  } catch {
    error.value = 'Unable to load search results.'
  } finally {
    loading.value = false
  }
}

function submitSearch() {
  router.push(searchUrl('films', 1, searchInput.value.trim()))
}

function setActiveType(type) {
  router.push(searchUrl(type))
}

function setPage(page) {
  router.push(pageUrl(page))
}

function openMobileSearchOverlay() {
  mobileSearchOpen.value = true
}

function closeMobileSearchOverlay() {
  mobileSearchOpen.value = false
}

watch(
  () => route.fullPath,
  () => {
    setDocumentTitle(buildDocumentTitle(['Search']))
    loadSearchResults()
  },
  { immediate: true },
)
</script>
